import React, { FC, useLayoutEffect, useRef, useState, useCallback } from 'react';
import { mergeWith } from 'lodash';
import useStyles, { smartFieldStyles } from '@components/SmartField/styles';
import Input from '@components/common/Input';
import {
  InputContainerRef,
  ISmartFieldEventData,
  ISmartFieldProps,
  ISmartFieldEvenDispatcher,
  ISmartFieldCustomEvent,
} from '@components/SmartField/types';
import debug from 'debug';

const error = debug('SmartField:error');

const SmartField: FC<ISmartFieldProps> = ({
  fields,
  placeholder = '\u0020', // invisible character because empty string display default dlocal placeholder
  type = 'card',
  styles,
  onFocus,
  onBlur,
  onChange,
  ...props
}) => {
  const css = useStyles();
  const [filledClassName, setFilledClassName] = useState('');
  const field = fields.create(type, {
    style: mergeWith({}, smartFieldStyles, styles),
    hideIcon: true,
    placeholder,
  });

  const focusHandler = (data: ISmartFieldCustomEvent) => {
    onFocus && onFocus(new CustomEvent('focus', data));
  };

  const blurHandler = (data: ISmartFieldCustomEvent) => {
    setFilledClassName(!data.detail.data.empty ? css.focused : '');
    onBlur && onBlur(new CustomEvent('blur', data));
  };

  const changeHandler = (data: ISmartFieldCustomEvent) => {
    setFilledClassName(!data.detail.data.empty ? css.focused : '');
    onChange && onChange(new CustomEvent('change', data));
  };

  const inputComponent = useCallback(({ inputRef, field, ...restProps }) => {
    const inputContainerRef: InputContainerRef = useRef(null);

    useLayoutEffect(() => {
      const dispatchEvent: ISmartFieldEvenDispatcher =
        (name, callback) => (data: ISmartFieldEventData) => {
          const detail = { data, field };
          if (callback) {
            const event = new CustomEvent(name, { detail });
            callback(event);
          }
        };
      try {
        field.mount(inputContainerRef.current);
        field.on('blur', dispatchEvent('blur', restProps.onBlur));
        field.on('focus', dispatchEvent('focus', restProps.onFocus));
        field.on('error', dispatchEvent('error', restProps.onError));
        field.on('complete', dispatchEvent('complete', restProps.onComplete));
        field.on('empty', dispatchEvent('empty', restProps.onEmpty));
        field.on('ready', dispatchEvent('empty', restProps.onReady));
        field.on('change', dispatchEvent('change', restProps.onChange));
        field.on('brand', dispatchEvent('brand', restProps.onBrand));
        field.on('autofilled', dispatchEvent('autofilled', restProps.onAutofilled));
      } catch (e) {
        error('Something went wrong mounting dlocal smart filed', e);
      }
    }, []);

    React.useImperativeHandle(inputRef, () => ({
      focus: () => {
        try {
          field.focus();
        } catch (e) {
          error('Something went wrong forwarding focus event to dlocal smart filed', e);
        }
      },
      blur: () => {
        try {
          field.blur();
        } catch (e) {
          error('Something went wrong forwarding blur event to dlocal smart filed', e);
        }
      },
    }));

    return (
      <div
        data-testid="dLocal-input-wrapper"
        className={css.dLocalInputWrapper}
        onClick={field.focus}
        ref={inputContainerRef}
      />
    );
  }, []);

  return (
    <Input
      {...props}
      data-testid="oyster-input"
      className={`${css.input} ${filledClassName}`}
      InputProps={{
        inputComponent,
        inputProps: {
          field,
          onChange: changeHandler,
          onBlur: blurHandler,
          onFocus: focusHandler,
        },
      }}
    />
  );
};

export default SmartField;
